import React, { useEffect, useMemo, useRef, useState } from 'react';
import Axios, { CancelToken } from 'axios';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import classnames from 'classnames';
import { z } from 'zod';
import { useSearchContext } from '../../../../../shared/contexts/SearchContextProvider';
import { ConsultaAPI } from '../../../../../shared/http-requests';
import * as CodEmpresaConst from '../../../../../shared/const/CodEmpresaConst';
import { ArrayHelper, Permissao } from '../../../../../shared/helpers';
import Button from '../../../../consulta-acessibilidade/Button';
import { useAccessibilityContext } from '../../../../consulta-acessibilidade/contexts/AccessibilityContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormControl, Col, Form, Row } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { RepositoryOptionType } from '../../../../../shared/types/RepositoryOptionType';
import RepositorySelectorV2 from '../../Home/v2/RepositorySelector';
import AppInfo from '../../../../../shared/components/AppInfo';

import { useAppContext } from '../../../../../shared/contexts/AppContextProvider';
import { changeSearch, limparFiltros } from '../../../../../store/ducks/pesquisa';
import eventBus from '../../../../../shared/events/GlobalEvent';
import AppSearchInout from '../../../../../shared/components/AppSearchInput';
import { usePesquisaPaginadaParams } from '../../../../../shared/hooks';

interface GeneralSearchProps {
    autoCompleteTerms: any;
    instituicaoRx: { codEmpresa: any };
    guiasPesquisaRx: any[];
    onChangeSearch: (data: { searchTerm: string; searchOption: string }) => void;
}

interface GeneralSearchOption {
    label: string;
    value: string;
    grupo: string;
}

const SEARCH_GENERAL_OPTION = 'LIVRE';
const LOCAL_REPOSITORY = 'local';

const schema = z.object({
    searchTerm: z.string().min(1, 'CampoBuscaObrigatorio'),
    searchOption: z
        .string()
        .min(1, 'É necessario informar a opção de pesquisa.'),
});

type FormData = z.infer<typeof schema>;

const GeneralSearch: React.FC<GeneralSearchProps> = ({
    instituicaoRx,
    guiasPesquisaRx,
    onChangeSearch,
    autoCompleteTerms,
}) => {
    const dispatch = useDispatch();

    const [searchOptions, setSearchOptions] = useState<
        GeneralSearchOption[] | undefined
    >(undefined);

    const { t } = useTranslation();
    const { searchInputRef } = useAccessibilityContext();

    const {
        search,
        loadingSearch,
        lastSearchForm,
        rep,
        changeRep,
    } = useSearchContext();

    const { selectedTab, lang } = useAppContext();

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const [repBases, setRepBases] = useState<any>();

    const [isLocal, setIsLocal] = useState<boolean>(() => {
        const repFromUrl = searchParams.get('rep');

        if (!repFromUrl || repFromUrl === LOCAL_REPOSITORY) {
            return true;
        }

        return false;
    });

    const { clearFilters } = usePesquisaPaginadaParams();

    const normalizeSearchOption = (value?: string | null) => {
        if (typeof value !== 'string') return SEARCH_GENERAL_OPTION;

        return value.split(',')[0] || SEARCH_GENERAL_OPTION;
    };

    const firstSelectableOption = useMemo(() => {
        if (!searchOptions?.length) return SEARCH_GENERAL_OPTION;

        return (
            searchOptions.find(item => item.value !== 'sep')?.value ||
            SEARCH_GENERAL_OPTION
        );
    }, [searchOptions]);

    const getValidSearchOption = (value?: string | null) => {
        if (!isLocal) {
            return SEARCH_GENERAL_OPTION;
        }

        const normalizedValue = normalizeSearchOption(value);

        if (
            searchOptions?.some(
                item => item.value !== 'sep' && item.value === normalizedValue,
            )
        ) {
            return normalizedValue;
        }

        return firstSelectableOption;
    };

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
        setValue,
    } = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            searchOption: getValidSearchOption(
                lastSearchForm?.coluna_um || searchParams.get('for'),
            ),
            searchTerm:
                lastSearchForm?.termo_pesquisa || searchParams.get('q') || '',
        },
    });

    const data = watch();
    const searchTerm = watch('searchTerm');
    const searchOption = watch('searchOption');

    useEffect(() => {
        if (onChangeSearch) {
            onChangeSearch(data);
        }
    }, [data.searchOption, data.searchTerm]);

    useEffect(() => {
        reset({
            searchOption: getValidSearchOption(
                lastSearchForm?.coluna_um || searchParams.get('for'),
            ),
            searchTerm:
                lastSearchForm?.termo_pesquisa || searchParams.get('q') || '',
        });
    }, [firstSelectableOption, lastSearchForm, reset, searchOptions, location.search]);

    useEffect(() => {
        if (rep) {
            setIsLocal(false);
        }
    }, [rep]);

    useEffect(() => {
        if (isLocal) {
            return;
        }

        if (searchOption !== SEARCH_GENERAL_OPTION) {
            setValue('searchOption', SEARCH_GENERAL_OPTION, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }
    }, [isLocal, searchOption, setValue]);

    /**
     * Opções de repositorios de base
     */
    const repOptions: RepositoryOptionType[] = useMemo(
        () =>
            [
                {
                    permission: 89,
                    label: Permissao.busca(89)?.descricao
                        ? Permissao.busca(89)?.descricao
                        : 'Rede Pergamum',
                    value: 'rede_pergamum',
                },
                {
                    permission: 118,
                    label: Permissao.busca(118)?.descricao
                        ? Permissao.busca(118)?.descricao
                        : 'ABNT',
                    value: 'abnt',
                },
                {
                    permission: 113,
                    label: Permissao.busca(113)?.descricao
                        ? Permissao.busca(113)?.descricao
                        : 'Pesquisa Target Gedweb',
                    value: 'target_gedweb',
                },
                {
                    permission: 108,
                    label: Permissao.busca(108)?.descricao
                        ? Permissao.busca(108)?.descricao
                        : 'Rede Pergamum OAI',
                    value: 'rede_pergamum_oai',
                },
                {
                    permission: 109,
                    label: Permissao.busca(109)?.descricao
                        ? Permissao.busca(109)?.descricao
                        : 'Rede OAI Local',
                    value: 'rede_oai_local',
                },
                {
                    permission: 96,
                    label: Permissao.busca(96)?.descricao
                        ? Permissao.busca(96)?.descricao
                        : 'Rede Ebsco',
                    value: 'rede_ebsco',
                },
            ].filter(({ permission }) => Permissao.tem(permission)),
        [],
    );

    useEffect(() => {
        const cancelTokenSrc = Axios.CancelToken.source();
        const { token } = cancelTokenSrc;

        (async () => {
            const data = await fetchSearchOptions(token);

            if (data) {
                let optionsAux = [
                    { label: t('LIVRE'), value: 'LIVRE', grupo: '0' },
                    {
                        label: `${t('TITULO')} ${t('PalavraQualquerPosicao')}`,
                        value: 'TITULO',
                        grupo: '1',
                    },
                    {
                        label: `${t('AUTOR')} ${t('PalavraQualquerPosicao')}`,
                        value: 'AUTOR',
                        grupo: '2',
                    },
                    {
                        label: `${t('ASSUNTO')} ${t('PalavraQualquerPosicao')}`,
                        value: 'ASSUNTO',
                        grupo: '3',
                    },
                ];

                if (instituicaoRx?.codEmpresa === CodEmpresaConst.CJF) {
                    optionsAux = optionsAux.concat([
                        {
                            label: t('LIVRE_SIMILAR'),
                            value: 'LIVRE_SIMILAR',
                            grupo: '0',
                        },
                        {
                            label: t('TITULO_SIMILAR'),
                            value: 'TITULO_SIMILAR',
                            grupo: '1',
                        },
                        {
                            label: t('AUTOR_SIMILAR'),
                            value: 'AUTOR_SIMILAR',
                            grupo: '2',
                        },
                        {
                            label: t('ASSUNTO_SIMILAR'),
                            value: 'ASSUNTO_SIMILAR',
                            grupo: '3',
                        },
                    ]);
                }

                optionsAux = optionsAux.concat(data);

                const groups = Array.from(
                    ArrayHelper.groupByProperty('grupo', optionsAux).values(),
                );

                optionsAux = groups
                    .map((x, i) =>
                        i !== groups.length - 1
                            ? x.concat({ label: '-------', value: 'sep' })
                            : x,
                    )
                    .flat();

                setSearchOptions(optionsAux);
            }
        })();

        return () => {
            cancelTokenSrc.cancel();
        };
    }, [lang]);

    const fetchSearchOptions = async (
        cancelToken?: CancelToken,
    ): Promise<GeneralSearchOption[] | null> => {
        try {
            const response = await ConsultaAPI.buscarOpcoesConsultaIndice(
                cancelToken,
            );

            const { data } = response;

            if (data) {
                (data as GeneralSearchOption[]).forEach(item => {
                    i18next.addResource(
                        'pt',
                        'translation',
                        item.value,
                        item.label,
                    );
                    i18next.addResource(
                        'en',
                        'translation',
                        item.value,
                        item.label,
                    );
                    i18next.addResource(
                        'es',
                        'translation',
                        item.value,
                        item.label,
                    );
                });
            }

            return data;
        } catch (error) {
            if (!Axios.isCancel(error))
                console.log('error on request service data: ', error);
        }

        return null;
    };

    const handleSearch = (data: FormData) => {
        (async () => {
            const selectedSearchOption = isLocal
                ? data.searchOption || SEARCH_GENERAL_OPTION
                : SEARCH_GENERAL_OPTION;

            let form: any = {
                termo_pesquisa: data.searchTerm,
            };

            // limpando filtros para que não haja erro na alteração do combo de indices.
            dispatch(limparFiltros());
            clearFilters();

            form.coluna_um = `${selectedSearchOption}${
                repBases !== undefined ? `,${repBases}` : ''
            }`;

            let urlParams: any = {
                for: selectedSearchOption,
                q: data.searchTerm,
            };

            if (rep) {
                form.base = rep;
                urlParams.rep = rep;

                if (repBases !== undefined) {
                    urlParams.repBases = repBases;
                }
            }

            if (guiasPesquisaRx.length) {
                const isPesquisaPaginada = !!guiasPesquisaRx.find(
                    guia =>
                        guia.codigo === selectedTab &&
                        guia.usa_consulta_paginada,
                );

                if (isPesquisaPaginada) {
                    const newParams = {
                        page: 1,
                        perPage: 20,
                    };

                    dispatch(changeSearch(false));

                    urlParams = {
                        ...urlParams,
                        ...newParams,
                    };

                    form = {
                        ...form,
                        ...newParams,
                    };
                }
            }

            await search(form, urlParams);
        })();
    };

    const formRef = useRef(null);

    useEffect(() => {
        const handleClearSearch = () => {
            reset({
                searchOption: SEARCH_GENERAL_OPTION,
                searchTerm: '',
            });
        };

        eventBus.on('CLEAR_SEARCH', handleClearSearch);

        // Cleanup: Remover o evento ao desmontar o componente
        return () => {
            eventBus.off('CLEAR_SEARCH', handleClearSearch);
        };
    }, [reset]);

    return (
        <Form ref={formRef} onSubmit={handleSubmit(handleSearch)}>
            <Row className="content__left__input p-3">
                <Col
                    md={11}
                    sm="12"
                    className="input-holder mt-3 mt-md-3 m-auto"
                >
                    <div className="rotulo">{t('CampoBusca')}</div>

                    <Row className="m-0 p-0">
                        <Col className="m-0 p-0" xs="10" md="11">
                            <AppSearchInout
                                //@ts-ignore
                                ref={searchInputRef}
                                onEnter={() => {
                                    handleSubmit(handleSearch)();
                                }}
                                placeholder={t('DigiteSuaBuscaAqui')}
                                onChange={value => {
                                    setValue('searchTerm', value);
                                }}
                                value={searchTerm}
                                autoCompleteData={
                                    autoCompleteTerms
                                        ? autoCompleteTerms.map(
                                              (x: any, i: number) => ({
                                                  key: `${x.word}-${i}`,
                                                  value: x.word,
                                              }),
                                          )
                                        : []
                                }
                            />
                        </Col>

                        <Col
                            className="d-flex justify-content-start align-items-center"
                            xs="2"
                            md="1"
                        >
                            <AppInfo message={t('MsgHelpIcon')} />
                        </Col>
                    </Row>

                    {errors.searchTerm ? (
                        <FormControl.Feedback type="invalid">
                            {t(errors.searchTerm?.message as any)}
                        </FormControl.Feedback>
                    ) : (
                        ''
                    )}
                </Col>

                <div className="col-md-11 m-auto px-0">
                    <div className="row px-0">
                        <Col
                            lg="2"
                            md="3"
                            sm="12"
                            className="input-holder mt-3"
                        >
                            <div className="rotulo">{t('PesquisarPor')}</div>

                            <FormControl
                                as="select"
                                className={classnames(
                                    'b-tema20 cortxt rad10 s14',
                                    {
                                        disabled: !isLocal,
                                    },
                                )}
                                {...register('searchOption')}
                                aria-disabled={!isLocal}
                                tabIndex={!isLocal ? -1 : undefined}
                                onMouseDown={event => {
                                    if (!isLocal) {
                                        event.preventDefault();
                                    }
                                }}
                                onKeyDown={event => {
                                    if (!isLocal) {
                                        event.preventDefault();
                                    }
                                }}
                                style={
                                    !isLocal
                                        ? {
                                              pointerEvents: 'none',
                                              opacity: 0.65,
                                          }
                                        : undefined
                                }
                            >
                                {searchOptions
                                    ? searchOptions.map((item, index) =>
                                          item.value === 'sep' ? (
                                              <option
                                                  disabled
                                                  key={`termo-${item.value}${index}`}
                                              >
                                                  {item.label}
                                              </option>
                                          ) : (
                                              <option
                                                  key={`termo-${item.value}`}
                                                  value={item.value}
                                              >
                                                  {item.label}
                                              </option>
                                          ),
                                      )
                                    : ''}
                            </FormControl>
                        </Col>

                        {repOptions?.length > 0 ? (
                            <Col
                                sm={12}
                                md={3}
                                lg={2}
                                className="input-holder mt-3"
                            >
                                <div className="rotulo">{t('TipoBase')}</div>

                                <RepositorySelectorV2
                                    value={searchParams.get('rep') ?? undefined}
                                    selectedTab="pesquisa_geral"
                                    repOptions={repOptions}
                                    onSelect={(value, aux) => {
                                        console.log(`selected: ${value} ${aux}`);

                                        const localSelecionado =
                                            value === LOCAL_REPOSITORY || !value;

                                        setIsLocal(localSelecionado);
                                        setRepBases(aux);

                                        changeRep(
                                            localSelecionado
                                                ? null
                                                : value,
                                        );

                                        if (!localSelecionado) {
                                            setValue(
                                                'searchOption',
                                                SEARCH_GENERAL_OPTION,
                                                {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                },
                                            );
                                        }
                                    }}
                                />
                            </Col>
                        ) : null}

                        <Col
                            md="2"
                            lg="2"
                            sm="12"
                            className={classnames(' mt-3 px-0 align-self-end')}
                        >
                            <Button
                                title="Pesquisar"
                                icon="search"
                                variant="primeiro ml-0 btn_responsivo"
                                type="submit"
                                disabled={loadingSearch}
                            />
                        </Col>
                    </div>
                </div>
            </Row>
        </Form>
    );
};

const mapStateToProps = (state: any) => ({
    instituicaoRx: state.instituicao.data,
    guiasPesquisaRx: state.pageConfig.guiasPesquisa,
});

export default connect(mapStateToProps)(GeneralSearch);